/**
 * MSW (Mock Service Worker) Handlers
 *
 * Mock GraphQL responses for development without Shopify store
 */

import { graphql, HttpResponse } from 'msw';
import { MOCK_PRODUCTS, findProductByHandle } from './mock-products.data';

type Variables = Record<string, unknown>;

interface MockMoney {
  amount: number;
  currencyCode: string;
  formatted: string;
}

// Mock delay to simulate network latency
const mockDelay = (ms: number = 300) =>
  new Promise((resolve) => setTimeout(resolve, ms));

// ============================================================================
// GraphQL Query Handlers
// ============================================================================

export const handlers = [
  // Query: getProductByHandle
  graphql.query('getProduct', async ({ variables }: { variables: Variables }) => {
    await mockDelay();

    const handle = variables.handle as string;
    const product = findProductByHandle(handle);

    if (!product) {
      return HttpResponse.json({
        data: {
          product: null,
        },
        errors: [
          {
            message: `Product not found: ${handle}`,
          },
        ],
      });
    }

    return HttpResponse.json({
      data: {
        product,
      },
    });
  }),

  // Query: getProducts
  graphql.query('getProducts', async () => {
    await mockDelay(500);

    return HttpResponse.json({
      data: {
        products: {
          edges: MOCK_PRODUCTS.map((product) => ({
            node: product,
            cursor: product.id,
          })),
          pageInfo: {
            hasNextPage: false,
            hasPreviousPage: false,
          },
          totalCount: MOCK_PRODUCTS.length,
        },
      },
    });
  }),

  // Query: searchProducts
  graphql.query('searchProducts', async ({ variables }: { variables: Variables }) => {
    await mockDelay(400);

    const query = (variables.query as string)?.toLowerCase() || '';
    const filteredProducts = MOCK_PRODUCTS.filter(
      (p) =>
        p.title.toLowerCase().includes(query) ||
        p.description.toLowerCase().includes(query) ||
        p.tags.some((t) => t.toLowerCase().includes(query))
    );

    return HttpResponse.json({
      data: {
        products: {
          edges: filteredProducts.map((product) => ({
            node: product,
            cursor: product.id,
          })),
          pageInfo: {
            hasNextPage: false,
            hasPreviousPage: false,
          },
          totalCount: filteredProducts.length,
        },
      },
    });
  }),

  // Mutation: cartCreate
  graphql.mutation('cartCreate', async ({ variables }: { variables: Variables }) => {
    await mockDelay();

    const input = (variables as any).input;
    const lines = input?.lines as Array<{ merchandiseId: string; quantity: number }>;

    // Create mock cart lines
    const cartLines = lines?.map((line) => {
      const variantId = line.merchandiseId;
      const product = MOCK_PRODUCTS.find((p) =>
        p.variants.some((v) => v.id === variantId)
      );
      const variant = product?.variants.find((v) => v.id === variantId);

      if (!product || !variant) {
        throw new Error(`Variant not found: ${variantId}`);
      }

      return {
        id: `gid://shopify/CartLine/${Math.random()}`,
        quantity: line.quantity,
        merchandise: {
          id: variant.id,
          title: variant.title,
          product: {
            id: product.id,
            title: product.title,
            handle: product.handle,
            featuredImage: product.featuredImage,
          },
          selectedOptions: variant.selectedOptions,
          price: variant.price,
          compareAtPrice: variant.compareAtPrice,
        },
      };
    });

    const subtotalAmount = cartLines?.reduce((sum, line) => {
      const price = (line.merchandise.price as MockMoney).amount;
      return sum + price * line.quantity;
    }, 0) || 0;

    return HttpResponse.json({
      data: {
        cartCreate: {
          cart: {
            id: `gid://shopify/Cart/${Math.random()}`,
            checkoutUrl: 'https://checkout.shopify.com',
            lines: cartLines,
            cost: {
              subtotalAmount: {
                amount: subtotalAmount,
                currencyCode: 'EUR',
                formatted: new Intl.NumberFormat('it-IT', {
                  style: 'currency',
                  currency: 'EUR',
                }).format(subtotalAmount),
              },
              totalAmount: {
                amount: subtotalAmount,
                currencyCode: 'EUR',
                formatted: new Intl.NumberFormat('it-IT', {
                  style: 'currency',
                  currency: 'EUR',
                }).format(subtotalAmount),
              },
              totalTaxAmount: {
                amount: 0,
                currencyCode: 'EUR',
                formatted: '€0.00',
              },
            },
          },
          userErrors: [],
        },
      },
    });
  }),

  // Mutation: cartLinesAdd
  graphql.mutation('cartLinesAdd', async ({ variables }: { variables: Variables }) => {
    await mockDelay();

    const lines = variables.lines as Array<{ merchandiseId: string; quantity: number }>;

    // Create mock cart lines
    const cartLines = lines?.map((line) => {
      const variantId = line.merchandiseId;
      const product = MOCK_PRODUCTS.find((p) =>
        p.variants.some((v) => v.id === variantId)
      );
      const variant = product?.variants.find((v) => v.id === variantId);

      if (!product || !variant) {
        throw new Error(`Variant not found: ${variantId}`);
      }

      return {
        id: `gid://shopify/CartLine/${Math.random()}`,
        quantity: line.quantity,
        merchandise: {
          id: variant.id,
          title: variant.title,
          product: {
            id: product.id,
            title: product.title,
            handle: product.handle,
            featuredImage: product.featuredImage,
          },
          selectedOptions: variant.selectedOptions,
          price: variant.price,
          compareAtPrice: variant.compareAtPrice,
        },
      };
    });

    return HttpResponse.json({
      data: {
        cartLinesAdd: {
          cart: {
            id: variables.cartId as string,
            lines: cartLines,
            cost: {
              subtotalAmount: {
                amount: 0,
                currencyCode: 'EUR',
                formatted: '€0.00',
              },
              totalAmount: {
                amount: 0,
                currencyCode: 'EUR',
                formatted: '€0.00',
              },
              totalTaxAmount: {
                amount: 0,
                currencyCode: 'EUR',
                formatted: '€0.00',
              },
            },
          },
          userErrors: [],
        },
      },
    });
  }),

  // Mutation: cartLinesUpdate
  graphql.mutation('cartLinesUpdate', async () => {
    await mockDelay();

    return HttpResponse.json({
      data: {
        cartLinesUpdate: {
          cart: {
            id: 'gid://shopify/Cart/mock',
            lines: [],
            cost: {
              subtotalAmount: {
                amount: 0,
                currencyCode: 'EUR',
                formatted: '€0.00',
              },
              totalAmount: {
                amount: 0,
                currencyCode: 'EUR',
                formatted: '€0.00',
              },
              totalTaxAmount: {
                amount: 0,
                currencyCode: 'EUR',
                formatted: '€0.00',
              },
            },
          },
          userErrors: [],
        },
      },
    });
  }),

  // Mutation: cartLinesRemove
  graphql.mutation('cartLinesRemove', async () => {
    await mockDelay();

    return HttpResponse.json({
      data: {
        cartLinesRemove: {
          cart: {
            id: 'gid://shopify/Cart/mock',
            lines: [],
            cost: {
              subtotalAmount: {
                amount: 0,
                currencyCode: 'EUR',
                formatted: '€0.00',
              },
              totalAmount: {
                amount: 0,
                currencyCode: 'EUR',
                formatted: '€0.00',
              },
              totalTaxAmount: {
                amount: 0,
                currencyCode: 'EUR',
                formatted: '€0.00',
              },
            },
          },
          userErrors: [],
        },
      },
    });
  }),

  // Query: getProductRecommendations
  graphql.query('getProductRecommendations', async ({ variables }: { variables: Variables }) => {
    await mockDelay();

    const productId = variables.productId as string;
    // Return random products as recommendations
    const recommendedProducts = MOCK_PRODUCTS.filter((p) => p.id !== productId).slice(0, 4);

    return HttpResponse.json({
      data: {
        recommendedProducts: recommendedProducts.map((product) => ({
          ...product,
          // Add recommendation metadata
          reason: 'Prodotto consigliato',
        })),
      },
    });
  }),

  // Query: collections
  graphql.query('getCollections', async () => {
    await mockDelay();

    const collections = [
      {
        id: 'gid://shopify/Collection/1',
        handle: 'proteine',
        title: 'Proteine',
        description: 'Integratori proteici per la crescita muscolare',
      },
      {
        id: 'gid://shopify/Collection/2',
        handle: 'creatina',
        title: 'Creatina',
        description: 'Creatina per forza e potenza',
      },
      {
        id: 'gid://shopify/Collection/3',
        handle: 'aminoacidi',
        title: 'Aminoacidi',
        description: 'BCAA e aminoacidi essenziali',
      },
      {
        id: 'gid://shopify/Collection/4',
        handle: 'pre-workout',
        title: 'Pre-Workout',
        description: 'Energia e focus per l\'allenamento',
      },
    ];

    return HttpResponse.json({
      data: {
        collections: {
          edges: collections.map((collection) => ({
            node: collection,
            cursor: collection.id,
          })),
          pageInfo: {
            hasNextPage: false,
            hasPreviousPage: false,
          },
        },
      },
    });
  }),
];
